import { getApcaContrast } from './apca.ts';
import { parseEmailInteraction, type EmailInteraction } from './emailInteraction.ts';
export type SponsorsStylePresetConfiguration = {
  schemaVersion: 5;
  fontAssets: Array<{
    family: string;
    format: "woff2" | "woff" | "truetype" | "opentype";
    storageBucket: string;
    storagePath: string;
  }>;
  web: {
    customStyleEnabled: boolean;
    themeMode: "light" | "dark" | "device";
    themeRecipe: unknown;
    leaderboardSignInEnabled: boolean;
  };
  email: {
    interaction?: EmailInteraction;
    styleSource: "web" | "custom";
    customStyleEnabled: boolean;
    styleTokens: Record<string, string>;
    matchedStyleTokens: Record<string, string>;
    template: {
      preheader: string;
      heading: string;
      introText: string;
      footerText: string;
      ctaText: string;
      answerButtonStyle: "solid" | "outline";
    };
  };
  sponsorship: {
    sponsoredByEnabled: boolean;
    sponsoredByText: string;
    sponsoredByColor: string;
    footerUrl: string;
    logoStorageBucket: string | null;
    logoStoragePath: string | null;
    ctaEnabled: boolean;
    ctaImageStorageBucket: string | null;
    ctaImageStoragePath: string | null;
    ctaImageAspectRatio: number | null;
    ctaTagline: string;
    ctaTitle: string;
    ctaDescription: string;
    ctaButtonText: string;
    ctaUrl: string;
  };
  emailSignup: {
    enabled: boolean;
    signupType: "button" | "embed";
    title: string;
    description: string;
    buttonCta: string;
    confirmationMessage: string;
    signupUrl: string;
    embedCode: string;
  };
};

export type QuizSponsorsStylePresetResolution = {
  quizId: string;
  partnerId: string | null;
  presetId: string | null;
  revisionId: string | null;
  effectiveRevisionKey: string;
  configuration: SponsorsStylePresetConfiguration;
};

export type SponsorsStylePresetAttribution = Pick<
  QuizSponsorsStylePresetResolution,
  "presetId" | "revisionId" | "effectiveRevisionKey"
>;

export type SponsorsStylePresetEmailAppearance = {
  interaction: EmailInteraction;
  customStyleEnabled: boolean;
  styleTokens: Record<string, string>;
  fontAssets: SponsorsStylePresetConfiguration["fontAssets"];
  attribution: SponsorsStylePresetAttribution | null;
};

export type PublicSponsorsStylePresetConfiguration = Omit<
  SponsorsStylePresetConfiguration,
  "email"
>;

const EMAIL_STYLE_TOKEN_KEYS = [
  "--accent-primary",
  "--bg-subtle",
  "--bg-raised",
  "--outline-default",
  "--glyph-primary",
  "--glyph-secondary",
  "--radius-md",
  "--font-family-title",
  "--font-family-body",
  "--font-size-body",
] as const;

const EMAIL_STYLE_TOKEN_KEY_SET = new Set<string>(EMAIL_STYLE_TOKEN_KEYS);
const HEX_COLOR = /^#([0-9a-f]{6})$/i;
const SHORT_HEX_COLOR = /^#([0-9a-f]{3})$/i;
const FONT_FAMILY_NAME = /^[A-Za-z0-9][A-Za-z0-9 -]{0,63}$/;
const COLOR_VALUE = /^(#[0-9a-fA-F]{3,8}|rgba?\s*\(|hsla?\s*\(|oklch\s*\()/;
const RADIUS_VALUE = /^\d+(\.\d+)?(px|rem)$/;
const DIMENSION_VALUE = /^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/;
const FONT_VALUE = /^['"a-zA-Z0-9\s,-]+$/;
const APCA_BODY_TEXT_MINIMUM_LC = 75;
const EMAIL_ANSWER_BACKGROUND = "#ffffff";

const FONT_PAIRINGS = {
  atrivial: { title: "Libre Franklin", body: "Lato" },
  modern: { title: "Manrope", body: "Inter" },
  editorial: { title: "Playfair Display", body: "Source Sans 3" },
  friendly: { title: "Poppins", body: "Nunito Sans" },
} as const;

const KNOWN_BODY_FONT_FAMILIES = new Set([
  "system-ui",
  "arial",
  "helvetica",
  "times new roman",
  "georgia",
  "verdana",
  "tahoma",
  "trebuchet ms",
  "courier new",
  "libre franklin",
  "lato",
  "ayer",
  "nib",
  "work sans",
  "lora",
  "dm sans",
  "poppins",
  "open sans",
  "roboto",
  "merriweather",
  "source sans 3",
  "nunito sans",
  "playfair display",
  "libre baskerville",
  "pt serif",
  "inter",
  "bitter",
  "manrope",
  "montserrat",
  "oswald",
  "raleway",
]);

const SERIF_FONT_FAMILIES = new Set([
  "times new roman",
  "georgia",
  "ayer",
  "nib",
  "lora",
  "merriweather",
  "playfair display",
  "libre baskerville",
  "pt serif",
  "bitter",
]);

type CanonicalThemeRecipe = {
  version: 2;
  editorMode: "simple" | "advanced";
  primaryColor: string;
  secondaryColor: string | null;
  outlineColor: string | null;
  successColor: string | null;
  criticalColor: string | null;
  backgroundColor: string | null;
  cornerStyle: "square" | "soft" | "rounded" | "custom";
  customRadiusPx: number | null;
  fontPairing: keyof typeof FONT_PAIRINGS;
  titleFontFamily: string | null;
  bodyFontFamily: string | null;
  surfaceTint: "neutral" | "subtle" | "bold";
  outerCanvas: "solid" | "transparent";
};

const DEFAULT_THEME_RECIPE: CanonicalThemeRecipe = {
  version: 2,
  editorMode: "simple",
  primaryColor: "#1F2937",
  secondaryColor: null,
  outlineColor: null,
  successColor: null,
  criticalColor: null,
  backgroundColor: null,
  cornerStyle: "soft",
  customRadiusPx: null,
  fontPairing: "atrivial",
  titleFontFamily: null,
  bodyFontFamily: null,
  surfaceTint: "neutral",
  outerCanvas: "solid",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return typeof value === "string" && allowed.includes(value as T)
    ? value as T
    : fallback;
}

function canonicalHex(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const full = trimmed.match(HEX_COLOR)?.[1];
  if (full) return `#${full.toUpperCase()}`;
  const short = trimmed.match(SHORT_HEX_COLOR)?.[1];
  if (!short) return null;
  return `#${short.split("").map((part) => `${part}${part}`).join("").toUpperCase()}`;
}

function safeFontFamily(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const family = value.trim();
  return family && FONT_FAMILY_NAME.test(family) ? family : null;
}

function sanitizeThemeRecipe(value: unknown): CanonicalThemeRecipe {
  const input = isRecord(value) ? value : {};
  const editorMode = oneOf(
    input.editorMode,
    ["simple", "advanced"] as const,
    DEFAULT_THEME_RECIPE.editorMode,
  );
  const cornerStyle = oneOf(
    input.cornerStyle,
    ["square", "soft", "rounded", "custom"] as const,
    DEFAULT_THEME_RECIPE.cornerStyle,
  );
  const effectiveCornerStyle = editorMode === "simple" && cornerStyle === "custom"
    ? DEFAULT_THEME_RECIPE.cornerStyle
    : cornerStyle;
  const radius = typeof input.customRadiusPx === "number" &&
      Number.isFinite(input.customRadiusPx)
    ? Math.min(24, Math.max(0, Math.round(input.customRadiusPx)))
    : null;
  const titleFontFamily = safeFontFamily(input.titleFontFamily);
  const bodyFontFamily = safeFontFamily(input.bodyFontFamily);

  return {
    version: 2,
    editorMode,
    primaryColor: canonicalHex(input.primaryColor) ?? DEFAULT_THEME_RECIPE.primaryColor,
    secondaryColor: editorMode === "advanced" ? canonicalHex(input.secondaryColor) : null,
    outlineColor: editorMode === "advanced" ? canonicalHex(input.outlineColor) : null,
    successColor: editorMode === "advanced" ? canonicalHex(input.successColor) : null,
    criticalColor: editorMode === "advanced" ? canonicalHex(input.criticalColor) : null,
    backgroundColor: editorMode === "advanced" ? canonicalHex(input.backgroundColor) : null,
    cornerStyle: effectiveCornerStyle,
    customRadiusPx: editorMode === "advanced" && effectiveCornerStyle === "custom"
      ? radius ?? 8
      : null,
    fontPairing: oneOf(
      input.fontPairing,
      Object.keys(FONT_PAIRINGS) as Array<keyof typeof FONT_PAIRINGS>,
      DEFAULT_THEME_RECIPE.fontPairing,
    ),
    titleFontFamily: editorMode === "advanced" ? titleFontFamily : null,
    bodyFontFamily: editorMode === "advanced" && bodyFontFamily &&
        KNOWN_BODY_FONT_FAMILIES.has(bodyFontFamily.toLowerCase())
      ? bodyFontFamily
      : null,
    surfaceTint: editorMode === "advanced"
      ? oneOf(
        input.surfaceTint,
        ["neutral", "subtle", "bold"] as const,
        DEFAULT_THEME_RECIPE.surfaceTint,
      )
      : DEFAULT_THEME_RECIPE.surfaceTint,
    outerCanvas: editorMode === "advanced"
      ? oneOf(
        input.outerCanvas,
        ["solid", "transparent"] as const,
        DEFAULT_THEME_RECIPE.outerCanvas,
      )
      : DEFAULT_THEME_RECIPE.outerCanvas,
  };
}

function isValidEmailStyleToken(key: string, value: string): boolean {
  if (!EMAIL_STYLE_TOKEN_KEY_SET.has(key)) return false;
  if (value === "none" || value === "transparent") return true;
  if (key === "--radius-md") return RADIUS_VALUE.test(value);
  if (key === "--font-size-body") return DIMENSION_VALUE.test(value);
  if (key === "--font-family-title" || key === "--font-family-body") {
    return FONT_VALUE.test(value);
  }
  return COLOR_VALUE.test(value);
}

function sanitizeEmailStyleTokens(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  const result: Record<string, string> = {};
  for (const [key, rawValue] of Object.entries(value)) {
    if (typeof rawValue !== "string") continue;
    const normalized = rawValue.trim();
    if (normalized && isValidEmailStyleToken(key, normalized)) {
      result[key] = normalized;
    }
  }
  return result;
}

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  return [1, 3, 5].map((offset) =>
    Number.parseInt(hex.slice(offset, offset + 2), 16)
  ) as Rgb;
}

function rgbToHex(rgb: Rgb): string {
  return `#${rgb.map((channel) =>
    Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, "0")
  ).join("")}`;
}

function mix(first: string, second: string, secondWeight: number): string {
  const left = hexToRgb(first);
  const right = hexToRgb(second);
  return rgbToHex(left.map((channel, index) =>
    channel * (1 - secondWeight) + right[index] * secondWeight
  ) as Rgb);
}

function absoluteApcaContrast(foreground: string, background: string): number {
  const color = (hex: string) => { const [r, g, b] = hexToRgb(hex); return { r, g, b }; };
  return Math.abs(getApcaContrast(color(foreground), color(background)));
}

function ensureApcaContrast(color: string, background: string, minimumLc: number): string {
  if (!HEX_COLOR.test(color) || !HEX_COLOR.test(background)) return color;
  if (absoluteApcaContrast(color, background) >= minimumLc) return color.toLowerCase();

  let best = color;
  let bestContrast = absoluteApcaContrast(color, background);
  for (let step = 1; step <= 20; step += 1) {
    const candidates = ["#000000", "#ffffff"].map((target) =>
      mix(color, target, step / 20)
    );
    for (const candidate of candidates) {
      const contrast = absoluteApcaContrast(candidate, background);
      if (contrast > bestContrast) {
        best = candidate;
        bestContrast = contrast;
      }
    }
    const valid = candidates.filter((candidate) =>
      absoluteApcaContrast(candidate, background) >= minimumLc
    );
    if (valid.length > 0) {
      return valid.sort((left, right) =>
        absoluteApcaContrast(right, background) - absoluteApcaContrast(left, background)
      )[0];
    }
  }
  return best;
}

function readableText(background: string): string {
  return ["#111318", "#ffffff"].sort((left, right) =>
    absoluteApcaContrast(right, background) - absoluteApcaContrast(left, background)
  )[0];
}

function resolvedRadius(recipe: CanonicalThemeRecipe): number {
  switch (recipe.cornerStyle) {
    case "square":
      return 0;
    case "rounded":
      return 16;
    case "custom":
      return recipe.customRadiusPx ?? 8;
    case "soft":
    default:
      return 8;
  }
}

function fontToken(family: string): string {
  const fallback = SERIF_FONT_FAMILIES.has(family.toLowerCase()) ||
      family.toLowerCase().includes("serif")
    ? "serif"
    : family.toLowerCase().includes("mono")
    ? "monospace"
    : "sans-serif";
  return family === "system-ui"
    ? `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, ${fallback}, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`
    : `'${family}', ${fallback}`;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNullableCtaImageAspectRatio(
  value: unknown,
): value is number | null {
  return (
    value === null ||
    (typeof value === "number" &&
      Number.isFinite(value) &&
      value > 0)
  );
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  );
}

function isFontAsset(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    typeof value.family === "string" &&
    (value.format === "woff2" ||
      value.format === "woff" ||
      value.format === "truetype" ||
      value.format === "opentype") &&
    typeof value.storageBucket === "string" &&
    typeof value.storagePath === "string"
  );
}

export function parseSponsorsStylePresetConfiguration(
  value: unknown,
): SponsorsStylePresetConfiguration | null {
  if (
    !isRecord(value) ||
    (value.schemaVersion !== 1 &&
      value.schemaVersion !== 2 &&
      value.schemaVersion !== 3 &&
      value.schemaVersion !== 4 &&
      value.schemaVersion !== 5) ||
    !Array.isArray(value.fontAssets) ||
    !value.fontAssets.every(isFontAsset)
  ) {
    return null;
  }

  const web = value.web;
  const email = value.email;
  const emailTemplate = isRecord(email) ? email.template : null;
  const sponsorship = value.sponsorship;
  const emailSignup = value.emailSignup;
  const usesModernEmailStyle =
    value.schemaVersion === 3 ||
    value.schemaVersion === 4 ||
    value.schemaVersion === 5;
  const usesCtaImageAspectRatio =
    value.schemaVersion === 4 || value.schemaVersion === 5;
  if (
    !isRecord(web) ||
    (value.schemaVersion !== 1 &&
      typeof web.customStyleEnabled !== "boolean") ||
    typeof web.leaderboardSignInEnabled !== "boolean" ||
    !isRecord(email) ||
    (usesModernEmailStyle &&
      email.styleSource !== "web" &&
      email.styleSource !== "custom") ||
    typeof email.customStyleEnabled !== "boolean" ||
    !isStringRecord(email.styleTokens) ||
    !parseEmailInteraction(email.interaction) ||
    (usesModernEmailStyle && !isStringRecord(email.matchedStyleTokens)) ||
    !isRecord(emailTemplate) ||
    !isRecord(sponsorship) ||
    !isRecord(emailSignup)
  ) {
    return null;
  }

  const isValid =
    (web.themeMode === "light" ||
      web.themeMode === "dark" ||
      web.themeMode === "device") &&
    typeof emailTemplate.preheader === "string" &&
    typeof emailTemplate.heading === "string" &&
    typeof emailTemplate.introText === "string" &&
    typeof emailTemplate.footerText === "string" &&
    typeof emailTemplate.ctaText === "string" &&
    (emailTemplate.answerButtonStyle === "solid" ||
      emailTemplate.answerButtonStyle === "outline") &&
    typeof sponsorship.sponsoredByEnabled === "boolean" &&
    typeof sponsorship.sponsoredByText === "string" &&
    typeof sponsorship.sponsoredByColor === "string" &&
    (value.schemaVersion !== 5 ||
      typeof sponsorship.footerUrl === "string") &&
    isNullableString(sponsorship.logoStorageBucket) &&
    isNullableString(sponsorship.logoStoragePath) &&
    typeof sponsorship.ctaEnabled === "boolean" &&
    isNullableString(sponsorship.ctaImageStorageBucket) &&
    isNullableString(sponsorship.ctaImageStoragePath) &&
    (!usesCtaImageAspectRatio ||
      isNullableCtaImageAspectRatio(sponsorship.ctaImageAspectRatio)) &&
    typeof sponsorship.ctaTagline === "string" &&
    typeof sponsorship.ctaTitle === "string" &&
    typeof sponsorship.ctaDescription === "string" &&
    typeof sponsorship.ctaButtonText === "string" &&
    typeof sponsorship.ctaUrl === "string" &&
    typeof emailSignup.enabled === "boolean" &&
    (emailSignup.signupType === "button" ||
      emailSignup.signupType === "embed") &&
    typeof emailSignup.title === "string" &&
    typeof emailSignup.description === "string" &&
    typeof emailSignup.buttonCta === "string" &&
    typeof emailSignup.confirmationMessage === "string" &&
    typeof emailSignup.signupUrl === "string" &&
    typeof emailSignup.embedCode === "string";
  if (!isValid) return null;

  const fontAssets = value.fontAssets
    .flatMap((asset): SponsorsStylePresetConfiguration["fontAssets"] => {
      if (!isRecord(asset)) return [];
      const family = typeof asset.family === "string" ? asset.family.trim() : "";
      const storageBucket = typeof asset.storageBucket === "string"
        ? asset.storageBucket.trim()
        : "";
      const storagePath = typeof asset.storagePath === "string"
        ? asset.storagePath.trim()
        : "";
      if (!family || !storageBucket || !storagePath || !isFontAsset(asset)) return [];
      return [{
        family,
        format: asset.format as SponsorsStylePresetConfiguration["fontAssets"][number]["format"],
        storageBucket,
        storagePath,
      }];
    })
    .sort((left, right) =>
      left.family.localeCompare(right.family, undefined, { sensitivity: "base" }) ||
      left.storagePath.localeCompare(right.storagePath)
    );
  const parsedWeb: SponsorsStylePresetConfiguration["web"] = {
    ...(web as Omit<SponsorsStylePresetConfiguration["web"], "customStyleEnabled">),
    customStyleEnabled: value.schemaVersion === 1
      ? true
      : web.customStyleEnabled as boolean,
    themeRecipe: sanitizeThemeRecipe(web.themeRecipe),
  };

  return {
    ...(value as Omit<
      SponsorsStylePresetConfiguration,
      "schemaVersion" | "web" | "email" | "sponsorship"
    >),
    schemaVersion: 5,
    fontAssets,
    web: parsedWeb,
    email: {
      ...(email as Omit<
        SponsorsStylePresetConfiguration["email"],
        "styleSource" | "matchedStyleTokens"
      >),
      ...(email.interaction !== undefined ? { interaction: parseEmailInteraction(email.interaction)! } : {}),
      styleTokens: sanitizeEmailStyleTokens(email.styleTokens),
      styleSource:
        usesModernEmailStyle
          ? email.styleSource === "web"
            ? "web"
            : "custom"
          : email.customStyleEnabled
            ? "custom"
            : "web",
      matchedStyleTokens:
        usesModernEmailStyle
          ? sanitizeEmailStyleTokens(email.matchedStyleTokens)
          : deriveSponsorsStylePresetMatchedEmailTokens(parsedWeb),
    },
    sponsorship: {
      ...(sponsorship as Omit<
        SponsorsStylePresetConfiguration["sponsorship"],
        "ctaImageAspectRatio" | "footerUrl"
      >),
      ctaImageAspectRatio:
        usesCtaImageAspectRatio
          ? (sponsorship.ctaImageAspectRatio as number | null)
          : null,
      footerUrl:
        typeof sponsorship.footerUrl === "string"
          ? (sponsorship.footerUrl as string)
          : "",
    },
  };
}

const REDACTED_EMAIL_PLACEHOLDER = {
  styleSource: "web",
  customStyleEnabled: false,
  styleTokens: {},
  matchedStyleTokens: {},
  template: {
    preheader: "",
    heading: "",
    introText: "",
    footerText: "",
    ctaText: "Play Now",
    answerButtonStyle: "solid",
  },
} as const;

/**
 * Normalizes the public runtime projection after its private Email object has
 * been removed. The placeholder exists only to reuse the complete canonical
 * configuration parser; it is discarded before this public value is returned.
 */
export function parsePublicSponsorsStylePresetConfiguration(
  value: unknown,
): PublicSponsorsStylePresetConfiguration | null {
  if (!isRecord(value)) return null;
  const parsed = parseSponsorsStylePresetConfiguration({
    ...value,
    email: REDACTED_EMAIL_PLACEHOLDER,
  });
  if (!parsed) return null;
  const { email: _privateEmail, ...publicConfiguration } = parsed;
  return publicConfiguration;
}

function emailTokensFromWebTheme(
  web: SponsorsStylePresetConfiguration["web"],
): Record<string, string> {
  if (!web.customStyleEnabled) return {};
  const recipe = sanitizeThemeRecipe(web.themeRecipe);
  const pairing = FONT_PAIRINGS[recipe.fontPairing];
  const titleFamily = recipe.titleFontFamily ?? pairing.title;
  const bodyFamily = recipe.bodyFontFamily ?? pairing.body;
  return {
    "--accent-primary": recipe.primaryColor,
    "--bg-subtle": recipe.primaryColor,
    "--bg-raised": EMAIL_ANSWER_BACKGROUND,
    "--outline-default": recipe.primaryColor,
    "--glyph-primary": readableText(recipe.primaryColor),
    "--glyph-secondary": ensureApcaContrast(
      recipe.primaryColor,
      EMAIL_ANSWER_BACKGROUND,
      APCA_BODY_TEXT_MINIMUM_LC,
    ),
    "--radius-md": `${resolvedRadius(recipe)}px`,
    "--font-family-title": fontToken(titleFamily),
    "--font-family-body": fontToken(bodyFamily),
  };
}

export function deriveSponsorsStylePresetMatchedEmailTokens(
  web: SponsorsStylePresetConfiguration["web"],
): Record<string, string> {
  const {
    "--bg-raised": _answerBackground,
    "--outline-default": _outline,
    ...persistedTokens
  } = emailTokensFromWebTheme(web);
  return persistedTokens;
}

function ensureMatchedAnswerContrast(tokens: Record<string, string>): Record<string, string> {
  const answerColor = tokens["--glyph-secondary"];
  if (!answerColor) return tokens;
  const answerBackground = tokens["--bg-raised"] ?? EMAIL_ANSWER_BACKGROUND;
  const readableAnswerColor = ensureApcaContrast(
    answerColor,
    answerBackground,
    APCA_BODY_TEXT_MINIMUM_LC,
  );
  return readableAnswerColor === answerColor
    ? tokens
    : { ...tokens, "--glyph-secondary": readableAnswerColor };
}

function selectedFontAssets(
  configuration: SponsorsStylePresetConfiguration,
  styleTokens: Record<string, string>,
): SponsorsStylePresetConfiguration["fontAssets"] {
  const referencedFamilies = new Set(
    [styleTokens["--font-family-title"], styleTokens["--font-family-body"]]
      .flatMap((value) => (value ?? "").split(","))
      .map((family) => family.trim().replace(/^['"]|['"]$/g, "").toLowerCase())
      .filter(Boolean),
  );
  const includedFamilies = new Set<string>();
  return configuration.fontAssets.filter((asset) => {
    const family = asset.family.toLowerCase();
    if (!referencedFamilies.has(family) || includedFamilies.has(family)) return false;
    includedFamilies.add(family);
    return true;
  }).slice(0, 2);
}

function isResolution(
  input: SponsorsStylePresetConfiguration | QuizSponsorsStylePresetResolution,
): input is QuizSponsorsStylePresetResolution {
  return "configuration" in input && isRecord(input.configuration);
}

export function resolveSponsorsStylePresetEmailAppearance(
  input: SponsorsStylePresetConfiguration | QuizSponsorsStylePresetResolution,
): SponsorsStylePresetEmailAppearance {
  const configuration = isResolution(input) ? input.configuration : input;
  const attribution = isResolution(input)
    ? {
      presetId: input.presetId,
      revisionId: input.revisionId,
      effectiveRevisionKey: input.effectiveRevisionKey,
    }
    : null;
  const email = configuration.email;
  const interaction = parseEmailInteraction(email.interaction)!;
  const customStyleEnabled = email.styleSource === "web"
    ? configuration.web.customStyleEnabled
    : email.customStyleEnabled;
  if (!customStyleEnabled) {
    return { interaction, customStyleEnabled: false, styleTokens: {}, fontAssets: [], attribution };
  }

  const webTokens = emailTokensFromWebTheme(configuration.web);
  const emailTokens = email.styleSource === "web"
    ? sanitizeEmailStyleTokens(email.matchedStyleTokens)
    : sanitizeEmailStyleTokens(email.styleTokens);
  const styleTokens = email.styleSource === "web"
    ? ensureMatchedAnswerContrast({
      ...webTokens,
      ...emailTokens,
      // These tokens describe the Live Quiz recipe itself, not an independently editable
      // matched Email snapshot. Persisted colors keep precedence; recipe geometry and fonts do not.
      ...(webTokens["--radius-md"] ? { "--radius-md": webTokens["--radius-md"] } : {}),
      ...(webTokens["--font-family-title"]
        ? { "--font-family-title": webTokens["--font-family-title"] }
        : {}),
      ...(webTokens["--font-family-body"]
        ? { "--font-family-body": webTokens["--font-family-body"] }
        : {}),
    })
    : { ...webTokens, ...emailTokens };
  return {
    interaction,
    customStyleEnabled: true,
    styleTokens,
    fontAssets: selectedFontAssets(configuration, styleTokens),
    attribution,
  };
}
